import React, { useState } from 'react'
import {
  Box,
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { keyBy } from 'lodash'
import { ArrowRight } from 'react-feather'
import { ProviderFragment } from '../../../generated/graphql'
import { EnergyType } from '../../../calculators/EnergyFlow'
import { ConsumptionType, createUsageMatrixUtils } from './usage-matrix-util'
import { SystemContainerCalculator } from '../../../calculators/SystemContainerCalculator'
import {
  MatrixDemand,
  UsageMatrixConnectionCell,
} from './UsageMatrixConnectionCell'
import { SpecialDemand } from '../../../calculators/special-demand-ids'
import { sortMatrixDemands } from '../../../demands/demand-util'

const useStyles = makeStyles((theme) => ({
  headerRow: {
    '& > .MuiTableCell-root': {
      background: theme.palette.grey[800],
      color: theme.palette.common.white,
    },
  },
  totalRow: {
    background: theme.palette.grey[300],
  },
  demandRow: {
    background: theme.palette.grey[50],
  },
}))

type Props = {
  energyType: EnergyType
  consumptionType: ConsumptionType
  hourOfYear: number
  calculator: SystemContainerCalculator
  providers: ProviderFragment[]
  demands: MatrixDemand[]
  onEditAllocation?: (
    provider: ProviderFragment,
    demandId: string,
    maxPortion: number,
  ) => void
}

export function UsageMatrixTable({
  energyType,
  consumptionType,
  hourOfYear,
  calculator,
  providers,
  demands,
  onEditAllocation,
}: Props): React.ReactElement {
  const classes = useStyles()
  const { t } = useTranslation()

  const [isEditing, setIsEditing] = useState(false)

  const calculatorProviders = calculator.getProviders()
  const calculatorProvidersById = keyBy(
    calculatorProviders,
    (provider) => provider.id,
  )

  const {
    formatTotalEnergyOrPowerByAllProviders,
    formatTotalEnergyOrPowerForProvider,
    formatTotalEnergyOrPowerForDemand,
    formatProvidedEnergyOrPower,
  } = createUsageMatrixUtils(
    calculatorProvidersById,
    calculator,
    energyType,
    consumptionType,
    hourOfYear,
  )

  const onToggleAllocation = (provider: ProviderFragment, demandId: string) => {
    if (!onEditAllocation) {
      return
    }

    const isConnected =
      demandId === SpecialDemand.OtherProvidersElectricity
        ? provider.providesElectricityForProviders
        : provider.demands.some(
            (providerDemand) => providerDemand.demand.id === demandId,
          )

    onEditAllocation(provider, demandId, isConnected ? 0 : 1)
  }

  return (
    <>
      <Table>
        <TableHead>
          <TableRow className={classes.headerRow}>
            <TableCell>{t('usageMatrix.demands')}</TableCell>
            <TableCell />
            {providers.map((provider) => (
              <TableCell key={provider.id} align="center">
                {provider.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow className={classes.totalRow}>
            <TableCell>
              {t(`usageMatrix.total.${energyType}`)}
              <br />
              {formatTotalEnergyOrPowerByAllProviders()}
            </TableCell>
            <TableCell>
              <ArrowRight />
            </TableCell>
            {providers.map((provider) => (
              <TableCell key={provider.id} align="center">
                {formatTotalEnergyOrPowerForProvider(provider.id)}
              </TableCell>
            ))}
          </TableRow>

          {demands.sort(sortMatrixDemands).map((demand) => (
            <TableRow key={demand.id} className={classes.demandRow}>
              <TableCell>
                {demand.name}
                <br />
                {formatTotalEnergyOrPowerForDemand(demand.id)}
              </TableCell>
              <TableCell>
                <ArrowRight />
              </TableCell>
              {providers.map((provider) => (
                <UsageMatrixConnectionCell
                  key={provider.id}
                  provider={provider}
                  demand={demand}
                  formatProvidedEnergyOrPower={formatProvidedEnergyOrPower}
                  onToggleAllocation={onToggleAllocation}
                  onEditAllocation={onEditAllocation}
                  isEditing={isEditing}
                />
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box
        mt={2}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body2">
          * {t('usageMatrix.asterixExplanation')}
        </Typography>

        <Button variant="outlined" onClick={() => setIsEditing(!isEditing)}>
          {isEditing
            ? t('usageMatrix.stopEditConnections')
            : t('usageMatrix.startEditConnections')}
        </Button>
      </Box>
    </>
  )
}
